import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { useNavigate } from "react-router-dom";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

/* ════════════════════════════════════════════════════════════════
   Fallback ads — used only if the API returns nothing for a slot
════════════════════════════════════════════════════════════════ */
const FALLBACK_WEB_ADS = [
  {
    id: "fallback-combo-1",
    bg: "var(--grad-primary)",
    headline: "Combo Machines",
    sub: "Snacks + drinks in one cabinet. From $1,950.",
    cta: "Shop Now →",
    link_url: "/shop?category=Combo+Snack%2BDrink",
    accent: "#f20089",
  },
  {
    id: "fallback-arcade-1",
    bg: "linear-gradient(160deg, #0a0018 0%, #3d0099 100%)",
    headline: "Arcade Ready",
    sub: "Claw, pinball, token dispensers in stock.",
    cta: "Explore →",
    link_url: "/shop?category=Arcade+Game",
    accent: "#f20089",
  },
];

const FALLBACK_MOBILE_ADS = [
  {
    id: "fallback-mob-1",
    headline: "Free Shipping",
    sub: "Orders over $3k ship free to the US.",
    cta: "Shop →",
    link_url: "/shop",
    bg: "var(--grad-primary)",
  },
  {
    id: "fallback-mob-2",
    headline: "Arcade Machines",
    sub: "Claw & arcade units in stock.",
    cta: "View →",
    link_url: "/shop?category=Arcade+Game",
    bg: "linear-gradient(135deg, #0a0018, #3d0099)",
  },
];

/* ════════════════════════════════════════════════════════════════
   Hook — fetch active ads for a given slot_type
════════════════════════════════════════════════════════════════ */
function useAds(slotType) {
  const [ads, setAds] = useState(null); // null = loading

  useEffect(() => {
    fetch(`${API}/ads?slot_type=${encodeURIComponent(slotType)}&active=true`)
      .then(r => r.json())
      .then(data => setAds(Array.isArray(data) ? data : []))
      .catch(() => setAds([]));
  }, [slotType]);

  return ads;
}

/* ════════════════════════════════════════════════════════════════
   WebAdBanner — 300×700 vertical banner, right rail
   slot: index into active web_sidebar ads (defaults to 0)
════════════════════════════════════════════════════════════════ */
export function WebAdBanner({ slot = 0 }) {
  const navigate = useNavigate();
  const dbAds = useAds("web_sidebar");

  // Still loading — render a placeholder to hold the space
  if (dbAds === null) {
    return (
      <div
        style={{
          width: 300,
          minHeight: 700,
          borderRadius: 20,
          background: "rgba(255,255,255,0.03)",
          border: "1px solid rgba(255,255,255,0.06)",
          flexShrink: 0,
          position: "sticky",
          top: 92,
        }}
      />
    );
  }

  // Use DB ads if available, otherwise fall back to hardcoded
  const pool = dbAds.length > 0 ? dbAds : FALLBACK_WEB_ADS;
  const ad = pool[slot % pool.length];

  const isDbAd = dbAds.length > 0;
  const headline = isDbAd ? ad.title : ad.headline;
  const sub      = isDbAd ? ad.alt_text : ad.sub;
  const cta      = isDbAd ? "Shop Now →" : ad.cta;
  const href     = isDbAd ? ad.link_url : ad.link_url;
  const bg       = isDbAd ? "var(--grad-primary)" : (ad.bg || "var(--grad-primary)");
  const accent   = isDbAd ? "#f20089" : (ad.accent || "#f20089");

  const handleClick = () => {
    if (!href) return;
    if (href.startsWith("http")) {
      window.open(href, "_blank", "noopener noreferrer");
    } else {
      navigate(href);
    }
  };

  return (
    <div
      onClick={handleClick}
      style={{
        width: 300,
        minHeight: 700,
        borderRadius: 20,
        cursor: href ? "pointer" : "default",
        position: "sticky",
        top: 92,
        flexShrink: 0,
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        boxShadow: "0 8px 40px rgba(106,0,244,0.25)",
        userSelect: "none",
        overflow: "hidden",
        // Background gradient always present — image layers on top
        background: bg,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-4px)";
        e.currentTarget.style.boxShadow = "0 16px 60px rgba(106,0,244,0.35)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "";
        e.currentTarget.style.boxShadow = "0 8px 40px rgba(106,0,244,0.25)";
      }}
    >
      {/* Full-bleed image — absolute, fills the entire card */}
      {isDbAd && ad.image_url && (
        <img
          src={ad.image_url}
          alt={ad.alt_text || ad.title || "Ad"}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center top",
            display: "block",
          }}
        />
      )}

      {/* Gradient scrim — ensures text is readable over any image */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: isDbAd && ad.image_url
            ? "linear-gradient(to bottom, rgba(0,0,0,0) 35%, rgba(0,0,0,0.72) 70%, rgba(0,0,0,0.88) 100%)"
            : "transparent",
          pointerEvents: "none",
        }}
      />

      {/* Content — pinned to the bottom when image present, centered otherwise */}
      <div
        style={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          height: "100%",
          minHeight: 700,
          padding: "28px 24px",
          justifyContent: isDbAd && ad.image_url ? "flex-end" : "center",
          gap: 14,
          boxSizing: "border-box",
        }}
      >
        {/* Decorative SVG — only when no image */}
        {(!isDbAd || !ad.image_url) && (
          <svg viewBox="0 0 80 120" style={{ width: 80, opacity: 0.75, flexShrink: 0 }} fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="8" y="4" width="64" height="112" rx="8" fill="white" fillOpacity="0.08"/>
            <rect x="14" y="12" width="52" height="36" rx="4" fill="white" fillOpacity="0.18"/>
            <line x1="20" y1="25" x2="60" y2="25" stroke={accent} strokeWidth="2" strokeLinecap="round" opacity="0.7"/>
            <line x1="20" y1="33" x2="50" y2="33" stroke="white" strokeWidth="1.5" strokeLinecap="round" opacity="0.4"/>
            <rect x="14" y="56" width="52" height="28" rx="3" fill="white" fillOpacity="0.08"/>
            <line x1="22" y1="63" x2="22" y2="77" stroke={accent} strokeWidth="1.5" opacity="0.5"/>
            <line x1="30" y1="63" x2="30" y2="77" stroke={accent} strokeWidth="1.5" opacity="0.5"/>
            <line x1="38" y1="63" x2="38" y2="77" stroke={accent} strokeWidth="1.5" opacity="0.5"/>
            <line x1="46" y1="63" x2="46" y2="77" stroke={accent} strokeWidth="1.5" opacity="0.5"/>
            <line x1="54" y1="63" x2="54" y2="77" stroke={accent} strokeWidth="1.5" opacity="0.5"/>
            <circle cx="24" cy="92" r="4" fill={accent} opacity="0.8"/>
            <circle cx="40" cy="92" r="4" fill="white" opacity="0.3"/>
            <circle cx="56" cy="92" r="4" fill="white" opacity="0.3"/>
            <rect x="14" y="104" width="52" height="6" rx="3" fill="white" fillOpacity="0.1"/>
          </svg>
        )}

        <div style={{ textAlign: "center", width: "100%" }}>
          <p
            style={{
              fontFamily: "'DM Mono', monospace",
              fontSize: 10,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: accent,
              marginBottom: 6,
              opacity: 0.9,
            }}
          >
            Vendbabee
          </p>
          {headline && (
            <h3
              style={{
                fontFamily: "'Oswald', sans-serif",
                fontWeight: 600,
                fontSize: "1.5rem",
                color: "#ffffff",
                lineHeight: 1.1,
                margin: "0 0 8px",
                letterSpacing: "-0.01em",
              }}
            >
              {headline}
            </h3>
          )}
          {sub && (
            <p
              style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: 13,
                color: "rgba(255,255,255,0.75)",
                lineHeight: 1.5,
                margin: 0,
              }}
            >
              {sub}
            </p>
          )}
        </div>

        {href && (
          <span
            style={{
              display: "inline-block",
              background: "rgba(255,255,255,0.18)",
              border: "1px solid rgba(255,255,255,0.3)",
              color: "#ffffff",
              borderRadius: 10,
              padding: "9px 20px",
              fontFamily: "'Inter', sans-serif",
              fontSize: 13,
              fontWeight: 600,
              letterSpacing: "0.02em",
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
            }}
          >
            {cta}
          </span>
        )}

        <p
          style={{
            fontFamily: "'DM Mono', monospace",
            fontSize: 9,
            color: "rgba(255,255,255,0.25)",
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            marginTop: isDbAd && ad.image_url ? 8 : "auto",
            paddingTop: isDbAd && ad.image_url ? 0 : 24,
          }}
        >
          Advertisement
        </p>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
   MobileAdToast — sticky bottom toast, mobile only
   Fetches mobile_toast slot from API, falls back to hardcoded.
   Auto-dismisses after 12s.
════════════════════════════════════════════════════════════════ */
export function MobileAdToast() {
  const navigate = useNavigate();
  const dbAds = useAds("mobile_toast");
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Pick a random fallback ad (stable across the session)
  const [fallbackIdx] = useState(() => Math.floor(Math.random() * FALLBACK_MOBILE_ADS.length));

  useEffect(() => {
    const show = setTimeout(() => setVisible(true), 3000);
    return () => clearTimeout(show);
  }, []);

  useEffect(() => {
    if (!visible) return;
    const hide = setTimeout(() => setVisible(false), 12000);
    return () => clearTimeout(hide);
  }, [visible]);

  if (dismissed || dbAds === null) return null;

  const pool = dbAds.length > 0 ? dbAds : FALLBACK_MOBILE_ADS;
  const raw  = pool[fallbackIdx % pool.length];
  const isDbAd = dbAds.length > 0;

  const headline = isDbAd ? raw.title    : raw.headline;
  const sub      = isDbAd ? raw.alt_text : raw.sub;
  const cta      = isDbAd ? "View →"    : raw.cta;
  const href     = isDbAd ? raw.link_url : raw.link_url;
  const bg       = isDbAd ? "var(--grad-primary)" : (raw.bg || "var(--grad-primary)");

  const handleClick = () => {
    if (href) {
      if (href.startsWith("http")) window.open(href, "_blank", "noopener noreferrer");
      else navigate(href);
    }
    setDismissed(true);
  };

  return (
    <div
      style={{
        position: "fixed",
        bottom: 20,
        right: 16,
        zIndex: 500,
        width: 250,
        height: 70,
        background: bg,
        borderRadius: 14,
        display: "flex",
        alignItems: "center",
        padding: "0 14px",
        gap: 10,
        boxShadow: "0 8px 32px rgba(106,0,244,0.35)",
        cursor: "pointer",
        transform: visible ? "translateY(0)" : "translateY(110px)",
        opacity: visible ? 1 : 0,
        transition: "transform 0.35s cubic-bezier(0.34,1.56,0.64,1), opacity 0.3s ease",
        pointerEvents: visible ? "all" : "none",
      }}
      onClick={handleClick}
    >
      <button
        onClick={(e) => { e.stopPropagation(); setDismissed(true); }}
        style={{
          position: "absolute",
          top: 6,
          right: 8,
          background: "rgba(255,255,255,0.15)",
          border: "none",
          borderRadius: "50%",
          width: 18,
          height: 18,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          padding: 0,
        }}
      >
        <X style={{ height: 10, width: 10, color: "rgba(255,255,255,0.7)" }} />
      </button>

      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 600, fontSize: 13, color: "#ffffff", margin: "0 0 2px", lineHeight: 1.2 }}>
          {headline}
        </p>
        <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 10, color: "rgba(255,255,255,0.65)", margin: 0, lineHeight: 1.35, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {sub}
        </p>
      </div>

      <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, fontWeight: 600, color: "#fff", background: "rgba(255,255,255,0.18)", borderRadius: 8, padding: "5px 10px", flexShrink: 0, whiteSpace: "nowrap" }}>
        {cta}
      </span>
    </div>
  );
}
